// src/pages/Services.js
import { useState, useRef } from 'react';
import { FiCheck, FiPlus, FiShoppingCart } from 'react-icons/fi';
import FadeIn from '../components/FadeIn';
import CartDrawer from '../components/CartDrawer';
import { useServices } from '../hooks/useServices';

function PackageThumbnail({ accent }) {
  return (
    <div className="pkg-browser">
      <div className="pkg-browser-bar">
        <span className="pkg-browser-dot" style={{ background: '#ef4444' }} />
        <span className="pkg-browser-dot" style={{ background: '#f59e0b' }} />
        <span className="pkg-browser-dot" style={{ background: '#22c55e' }} />
      </div>
      <div className="pkg-browser-body">
        <div className="pkg-browser-hero" style={{ background: `${accent}22`, borderBottom: `2px solid ${accent}33` }} />
        <div className="pkg-browser-lines">
          <span style={{ width: '60%', background: accent, opacity: 0.45 }} />
          <span style={{ width: '80%' }} />
          <span style={{ width: '70%' }} />
          <span style={{ width: '50%' }} />
        </div>
      </div>
    </div>
  );
}

function Services() {
  const { services, loading } = useServices();
  const PACKAGES = services.packages;
  const ADDONS = services.addons;
  const monthlyFee = services.monthlyFee;

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState(new Set());
  const [cartOpen, setCartOpen] = useState(false);
  const packagesRef = useRef(null);
  const [activePkg, setActivePkg] = useState(0);

  function handlePackagesScroll() {
    const el = packagesRef.current;
    if (!el) return;
    setActivePkg(Math.round(el.scrollLeft / el.offsetWidth));
  }

  function scrollToPkg(index) {
    const el = packagesRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.offsetWidth, behavior: 'smooth' });
    setActivePkg(index);
  }

  function togglePackage(id) {
    setSelectedPackage(prev => prev === id ? null : id);
  }

  function toggleAddon(id) {
    setSelectedAddons(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const cartCount = (selectedPackage ? 1 : 0) + selectedAddons.size;

  if (loading) return <div className="services-page"><p style={{ textAlign: 'center', padding: '4rem' }}>Loading…</p></div>;

  return (
    <div className="services-page">

      {/* ── Hero ── */}
      <section className="services-hero">
        <FadeIn>
          <p className="offer-heading">What We Build</p>
          <h1 className="services-hero-title">Website Packages</h1>
          <p className="services-hero-body">
            Choose the package that fits your goals, then add the extras you need.
            When you're ready, submit your selection and we'll be in touch within 48 hours.
          </p>
        </FadeIn>
      </section>

      {/* ── Packages ── */}
      <section className="packages-section">
        <FadeIn>
          <p className="offer-heading">Our Templates</p>
          <h2 className="offer-title">Choose Your Package</h2>
          <p className="services-hero-body" style={{ marginTop: '0.75rem', color: '#000' }}>
            The Landing Page and Standard Website packages include template previews — click "View Template" on each card to see an example before you choose.
          </p>
        </FadeIn>

        <div className="packages-grid" ref={packagesRef} onScroll={handlePackagesScroll}>
          {PACKAGES.map((pkg, i) => {
            const isSelected = selectedPackage === pkg.id;
            return (
              <FadeIn key={pkg.id} delay={i * 150}>
                <div
                  className={`package-card${isSelected ? ' package-card--selected' : ''}`}
                  style={isSelected ? { borderColor: pkg.accent, boxShadow: `0 0 0 2px ${pkg.accent}55` } : {}}
                >
                  {isSelected && (
                    <span className="package-selected-badge" style={{ background: pkg.accent }}>
                      <FiCheck size={12} /> Selected
                    </span>
                  )}
                  <PackageThumbnail accent={pkg.accent} />
                  <div className="package-card-body">
                    <span className="package-tier" style={{ color: pkg.accent }}>{pkg.tier}</span>
                    <h3 className="package-name">{pkg.name}</h3>
                    <p className="package-desc">{pkg.description}</p>
                    {pkg.subtext && <p className="package-subtext">{pkg.subtext}</p>}
                    <div className="package-footer">
                      <span className="package-price">{pkg.priceDisplay ?? `$${pkg.price.toLocaleString()}`}</span>
                      <div className="package-actions">
                        <button
                          className={`package-cart-btn${isSelected ? ' package-cart-btn--selected' : ''}`}
                          style={isSelected ? { background: pkg.accent, borderColor: pkg.accent } : {}}
                          onClick={() => togglePackage(pkg.id)}
                        >
                          {isSelected ? <><FiCheck size={13} /> Selected</> : <><FiPlus size={13} /> Add to Cart</>}
                        </button>
                        {pkg.previewTo && (
                          <a
                            href={pkg.previewTo}
                            className="package-preview-btn"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Template
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
        <div className="carousel-dots">
          {PACKAGES.map((_, i) => (
            <button
              key={i}
              className={`carousel-dot${activePkg === i ? ' carousel-dot--active' : ''}`}
              onClick={() => scrollToPkg(i)}
              aria-label={`Go to package ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ── Monthly Fee Notice ── */}
      <div className="monthly-fee-banner">
        <span className="monthly-fee-banner-highlight">${monthlyFee}/month</span>
        <span className="monthly-fee-banner-text">
          monthly subscription applies to all packages — full details are in the contract when you view your cart.
        </span>
      </div>

      {/* ── Add-ons ── */}
      <section className="addons-section">
        <FadeIn>
          <p className="offer-heading">Customise Your Build</p>
          <h2 className="addons-title">Additional Services</h2>
          <p className="addons-subtitle">
            Enhance any package with the following add-ons, each priced separately.
          </p>
        </FadeIn>

        <FadeIn delay={150}>
          <ul className="addons-list">
            {ADDONS.map(addon => {
              const isSelected = selectedAddons.has(addon.id);
              return (
                <li
                  key={addon.id}
                  className={`addon-item${isSelected ? ' addon-item--selected' : ''}`}
                  onClick={() => toggleAddon(addon.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && toggleAddon(addon.id)}
                >
                  <span className="addon-toggle">
                    {isSelected ? <FiCheck size={14} /> : <FiPlus size={14} />}
                  </span>
                  <span className="addon-label">{addon.label}</span>
                  <span className="addon-price">${addon.price.toLocaleString()}</span>
                </li>
              );
            })}
          </ul>
        </FadeIn>
      </section>

      {/* ── Floating Cart Button ── */}
      {cartCount > 0 && (
        <button className="cart-fab" onClick={() => setCartOpen(true)} aria-label="Open cart">
          <FiShoppingCart size={20} />
          <span className="cart-fab-label">View Selection</span>
          <span className="cart-fab-badge">{cartCount}</span>
        </button>
      )}

      {/* ── Cart Drawer ── */}
      {cartOpen && (
        <CartDrawer
          selectedPackage={selectedPackage}
          selectedAddons={selectedAddons}
          packages={PACKAGES}
          addons={ADDONS}
          onClose={() => setCartOpen(false)}
          onSubmitSuccess={() => {
            setSelectedPackage(null);
            setSelectedAddons(new Set());
          }}
        />
      )}

    </div>
  );
}

export default Services;
